/**
 * Primitive Farben - Die "rohen" Farbwerte
 *
 * Hier werden alle Farben genau EINMAL definiert, gruppiert nach Farbfamilie.
 * Diese Werte werden in colors.ts referenziert und sollten NIE direkt
 * in Komponenten verwendet werden.
 *
 * Benennungskonvention:
 * - Zahlen (50-900) orientieren sich an Material/Tailwind (höher = dunkler)
 */

// ===== TEAL (Brand Primary) - Hue: 185° (Cyan/Hellblau) =====
// Harmonisiert: Konsistente Lightness über alle Farbfamilien
export const teal = {
  50: "#e0f3f5", // L: 92% - Sehr hell (backgrounds)
  100: "#a8e8ef", // L: 82% - Pastell
  200: "#7dd6df", // L: 72% - Medium Pastell
  300: "#5fc9d3", // L: 64% - Light accent
  400: "#379aa6", // L: 48% - Medium
  500: "#20848f", // L: 42% - Main (primary.main)
  600: "#166d78", // L: 35% - Dark
  650: "#00a89a", // L: 33% - Brand Logo Accent (hohe Sättigung, vivid Cyan-Teal)
  700: "#105660", // L: 28% - Darker
  800: "#0a4048", // L: 20% - Very dark
  850: "#0d3b47", // L: 17% - Brand Logo Text
  900: "#062a30", // L: 14% - Darkest
} as const;

// ===== PETROL (Page header) - Hue: 190° =====
// Turquoise a touch bluer than the logo (teal[650]) – wind rather than water. Deep end for the
// gradient of the page header, light end for its accents.
export const petrol = {
  100: "#c2edf3", // Light text on the dark header
  300: "#66cfdf", // Glow and secondary text on the dark header
  400: "#2fb3c8", // Done line and dots
  500: "#1597ad", // Accent, active tab
  600: "#0e7488", // Gradient end
  800: "#073a4a", // Gradient middle
  900: "#04222d", // Gradient start
} as const;

// ===== GRÜN - Hue: 82° (Limetten-Grün) =====
// Harmonisiert: Konsistente Lightness über alle Stufen
export const green = {
  50: "#f0f5e5", // L: 92% - Sehr hell
  100: "#d4edaf", // L: 82% - Pastell
  200: "#b3d87a", // L: 72% - Medium Pastell
  300: "#98c85c", // L: 64% - Light accent
  400: "#6a9c3d", // L: 48% - KPI: akzeptabel
  500: "#5a8532", // L: 42% - KPI: gut
  600: "#4a6e28", // L: 35% - success main
  700: "#3a5720", // L: 28% - Darker
  800: "#2f4619", // L: 20% - Map: Schutzgebiete
  900: "#1f2e10", // L: 14% - KPI: sehr gut
} as const;

// ===== ROT - Hue: 0° =====
// Harmonisiert: Konsistente Lightness über alle Stufen
export const red = {
  50: "#f5e5e5", // L: 92% - Sehr hell
  100: "#f5b0b0", // L: 82% - Pastell
  200: "#e88a8a", // L: 72% - Medium Pastell
  300: "#d97a7a", // L: 64% - Light accent
  400: "#a64040", // L: 48% - Map: Infrastruktur
  500: "#8f3232", // L: 42% - error main
  600: "#782828", // L: 35% - KPI: inakzeptabel
  700: "#601f1f", // L: 28% - secondary.main
  800: "#541515", // L: 20% - Very dark
  900: "#331010", // L: 14% - KPI: nicht tragfähig
} as const;

// ===== ORANGE - Hue: 30° =====
// Harmonisiert: Konsistente Lightness über alle Stufen
export const orange = {
  50: "#f5ede5", // L: 92% - Sehr hell
  100: "#FFD4A3", // L: 82% - Pastell (Parcel: Kein Kontakt)
  200: "#e8b87a", // L: 72% - Medium Pastell
  300: "#d9a87a", // L: 64% - Light accent
  400: "#a67040", // L: 48% - Medium
  500: "#8f5c32", // L: 42% - Map: Flurstücke, Charts
  600: "#784a28", // L: 35% - warning main
  700: "#60391f", // L: 28% - KPI: sehr kritisch
  800: "#542b15", // L: 20% - Very dark
  900: "#3b1d0e", // L: 14% - Darkest
} as const;

// ===== GELB - Hue: 50° =====
// Harmonisiert: Konsistente Lightness über alle Stufen
export const yellow = {
  50: "#f5f3e5", // L: 92% - Sehr hell
  100: "#FFF4A3", // L: 82% - Pastell (Parcel: Kontaktiert)
  200: "#e8d87a", // L: 72% - Medium Pastell
  300: "#d9c87a", // L: 64% - Light accent
  400: "#a69040", // L: 48% - KPI: Minimum
  500: "#8f7a32", // L: 42% - Highlights
  600: "#786428", // L: 35% - Highlight dark
  700: "#5c4e20", // L: 28% - Darker
  800: "#423815", // L: 20% - Very dark
  900: "#2e270e", // L: 14% - Darkest
} as const;

// ===== BLAU - Hue: 210° =====
// Harmonisiert: Konsistente Lightness über alle Stufen
export const blue = {
  50: "#e5edf5", // L: 92% - Sehr hell
  100: "#b0cff5", // L: 82% - Pastell (Chart P50, Marktpreis)
  200: "#7aaee8", // L: 72% - Medium Pastell
  300: "#5c9ad9", // L: 64% - Sky-Blau (Cashflow-Linien)
  400: "#4078a6", // L: 48% - Map: Siedlungen
  500: "#32658f", // L: 42% - info main, focus, border
  600: "#285278", // L: 35% - Gradient dunkel
  700: "#1a4270", // L: 28% - Darker
  800: "#1a3650", // L: 20% - utility.darkBlue
  900: "#0d2138", // L: 14% - Darkest
} as const;

// ===== GRAU (vereinfachte Skala) =====
export const gray = {
  0: "#ffffff", // Weiß (paper, card)
  50: "#fcfcfc", // Fast weiß (background.default)
  100: "#F8FAFC", // Sehr hell - Slate (tableHeader)
  150: "#F1F5F9", // Sehr hell - Slate (background)
  175: "#E8EEF4", // Hell - Slate-Blau (Pre-Betriebsjahr Header)
  200: "#e0e0e0", // Hell (border)
  250: "#E2E8F0", // Hell - Slate (disabled text in tables)
  300: "#cccccc", // Mittel-hell (chart border)
  400: "#9e9e9e", // Mittel (disabled text, KPI noData)
  500: "#808080", // Mittel (Map: bestehende Parks)
  600: "#64748B", // Mittel-dunkel - Slate (text.secondary)
  700: "#4A4A4A", // Dunkel (text.primary)
  750: "#374151", // Dunkel (chart.text)
  800: "#333333", // Sehr dunkel (chart fill)
  850: "#262626", // Sehr dunkel (shadow.defaultHover)
  900: "#1A1A1A", // Sehr dunkel (shadow.default)
  950: "#161616", // Fast schwarz (border.medium)
  1000: "#000000", // Schwarz
} as const;

// ===== TRANSPARENZEN (als Funktion für Flexibilität) =====
export const alpha = {
  // Schwarz mit Transparenz
  black02: "rgba(0, 0, 0, 0.02)",
  black04: "rgba(0, 0, 0, 0.04)",
  black05: "rgba(0, 0, 0, 0.05)",
  black06: "rgba(0, 0, 0, 0.06)",
  black08: "rgba(0, 0, 0, 0.08)",
  black10: "rgba(0, 0, 0, 0.10)",
  black12: "rgba(0, 0, 0, 0.12)",
  black15: "rgba(0, 0, 0, 0.15)",
  black23: "rgba(0, 0, 0, 0.23)",
  black30: "rgba(0, 0, 0, 0.30)",
  black38: "rgba(0, 0, 0, 0.38)",
  black50: "rgba(0, 0, 0, 0.50)",

  // Weiß mit Transparenz (für Glassmorphism)
  white05: "rgba(255, 255, 255, 0.05)",
  white08: "rgba(255, 255, 255, 0.08)",
  white10: "rgba(255, 255, 255, 0.10)",
  white12: "rgba(255, 255, 255, 0.12)",
  white15: "rgba(255, 255, 255, 0.15)",
  white18: "rgba(255, 255, 255, 0.18)",
  white20: "rgba(255, 255, 255, 0.20)",
  white25: "rgba(255, 255, 255, 0.25)",
  white30: "rgba(255, 255, 255, 0.30)",
  white35: "rgba(255, 255, 255, 0.35)",
  white70: "rgba(255, 255, 255, 0.70)",
  white80: "rgba(255, 255, 255, 0.80)",
  white90: "rgba(255, 255, 255, 0.90)",
  white95: "rgba(255, 255, 255, 0.95)",

  // Grau mit Transparenz (für Disabled-States)
  gray50: "rgba(200, 200, 200, 0.5)",

  // Schwarz semi-opak (für Tooltips)
  black85: "rgba(0, 0, 0, 0.85)",

  // Blau mit Transparenz (für Primary-States) - blue[500]: #32658f
  blue04: "rgba(50, 101, 143, 0.04)",
  blue05: "rgba(50, 101, 143, 0.05)",
  blue08: "rgba(50, 101, 143, 0.08)",
  blue15: "rgba(50, 101, 143, 0.15)",
  blue30: "rgba(50, 101, 143, 0.30)",

  // Grün mit Transparenz (für Success-States) - green[600]: #4a6e28
  green08: "rgba(74, 110, 40, 0.08)",

  // Rot mit Transparenz (für Error-States) - red[500]: #8f3232
  red08: "rgba(143, 50, 50, 0.08)",
  red12: "rgba(143, 50, 50, 0.12)",

  // Orange mit Transparenz (für Warning-States) - orange[600]: #784a28
  orange08: "rgba(120, 74, 40, 0.08)",

  // Rot-Pastell mit Transparenz (Flurstück abgelehnt) - red[100]: #f5b0b0
  redTint15: "rgba(245, 176, 176, 0.15)",

  // Gelb-Pastell mit Transparenz (Flurstück kontaktiert) - yellow[100]: #FFF4A3
  yellowTint20: "rgba(255, 244, 163, 0.20)",

  // Petrol mit Transparenz (Seitenkopf) - petrol[300]: #66cfdf, petrol[500]: #1597ad
  petrolGlow35: "rgba(102, 207, 223, 0.35)",
  petrolShadow40: "rgba(21, 151, 173, 0.40)",
  petrolSpot60: "rgba(21, 151, 173, 0.60)",
  // Petrol-Dunkel mit Transparenz (Schatten unter dem Seitenkopf) - petrol[900]: #04222d
  petrolDark18: "rgba(4, 34, 45, 0.18)",

  // Gelb mit Transparenz (für Highlights) - yellow[500]: #8f7a32
  yellow08: "rgba(143, 122, 50, 0.08)",
} as const;

// Typ-Exports für TypeScript-Unterstützung
export type TealColor = typeof teal;
export type GreenColor = typeof green;
export type RedColor = typeof red;
export type OrangeColor = typeof orange;
export type YellowColor = typeof yellow;
export type BlueColor = typeof blue;
export type GrayColor = typeof gray;
export type AlphaColor = typeof alpha;
